/**
 * @fileoverview SecureFullscreenVideo Component
 * 
 * A secure fullscreen video player using CloudFront + Lambda@Edge authorization.
 * 
  * @description Security Features:
 * - Videos served directly from CloudFront (https://video.ait.care)
 * - Lambda@Edge validates id_token cookie on every request
 * - Authorization checks: JWT signature, expiration, facility_id match
 * - Disables right-click context menu
 * - Disables download button in controls
 * - Disables picture-in-picture mode
 * - URLs cannot be shared (require valid cookie)
 * 
 * @exports SecureFullscreenVideo
 * @requires react
 */

import { useRef, useState, useEffect } from 'react';

const W = "#ffffff";

/**
 * SecureFullscreenVideo - Fullscreen video player with CloudFront + Lambda@Edge security
 * 
 * @component
 * @param {Object} props - Component props
 * @param {string} props.videoUrl - CloudFront video URL (https://video.ait.care/...)
 * @param {Object|null} props.fallInfo - Fall incident information
 * @param {Function} props.onClose - Callback to close the fullscreen modal
 * @param {Function} props.formatDateTime - Function to format timestamps
 * 
 * @returns {JSX.Element} Fullscreen video modal with security features
 */
export default function SecureFullscreenVideo({ videoUrl, fallInfo, onClose, formatDateTime }) {
  const videoRef = useRef(null);
  const [videoError, setVideoError] = useState(null);

  // Set up error handling for video
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    setVideoError(null);

    const handleError = (e) => {
      console.error('[SecureFullscreenVideo] Video error:', e);
      console.error('[SecureFullscreenVideo] Video error details:', {
        error: video.error,
        networkState: video.networkState,
        readyState: video.readyState,
        src: video.src
      });
      
      let errorMessage = 'Failed to load video';
      if (video.error) {
        switch (video.error.code) {
          case 1:
            errorMessage = 'Video loading aborted';
            break;
          case 2:
            errorMessage = 'Network error while loading video';
            break;
          case 3:
            errorMessage = 'Video decoding error';
            break;
          case 4:
            errorMessage = 'Video format not supported or unauthorized';
            break;
          default:
            errorMessage = `Video error (code: ${video.error.code})`;
        }
      }
      setVideoError(errorMessage);
    };

    const handleLoadStart = () => {
      console.log('[SecureFullscreenVideo] Video load started:', videoUrl);
    };

    const handleCanPlay = () => {
      console.log('[SecureFullscreenVideo] Video can play');
    };

    video.addEventListener('error', handleError);
    video.addEventListener('loadstart', handleLoadStart);
    video.addEventListener('canplay', handleCanPlay);

    return () => {
      video.removeEventListener('error', handleError);
      video.removeEventListener('loadstart', handleLoadStart);
      video.removeEventListener('canplay', handleCanPlay);
    };
  }, [videoUrl]);



  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0, 0, 0, 0.98)",
        zIndex: 2000,
        display: "flex",
        flexDirection: "column",
        padding: 0,
      }}
      onClick={onClose}
    >
      {/* Modal Header */}
      <div 
        style={{ 
          display: "flex", 
          justifyContent: "space-between", 
          alignItems: "center",
          padding: "20px 40px",
          background: "rgba(10, 26, 48, 0.95)",
          borderBottom: "2px solid rgba(255, 255, 255, 0.2)",
          flexShrink: 0,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: W }}>
          Fall Video - Full Screen
          {fallInfo && formatDateTime && (
            <span style={{ fontSize: 14, opacity: 0.7, fontWeight: 400, marginLeft: 12 }}>
              {fallInfo.resident_name} - {formatDateTime(fallInfo.fall_ts).date} {formatDateTime(fallInfo.fall_ts).time}
            </span>
          )}
        </h3>
        <button
          onClick={onClose}
          style={{
            background: "rgba(255, 255, 255, 0.1)",
            border: "2px solid #ffffff",
            borderRadius: 8,
            color: W,
            fontSize: 28,
            cursor: "pointer",
            lineHeight: 1,
            width: 40,
            height: 40,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "all 0.2s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)";
          }}
        >
          ×
        </button>
      </div>

      {/* Video Container */}
      <div 
        style={{ 
          flex: 1, 
          display: "flex", 
          alignItems: "center", 
          justifyContent: "center",
          padding: 20,
          minHeight: 0,
          position: "relative",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Video Player - Direct CloudFront URL with Lambda@Edge authorization */}
        {videoUrl ? (
          <>
            {!videoError ? (
              <>
                <video
                  ref={videoRef}
                  src={videoUrl}
                  controls
                  autoPlay
                  crossOrigin="use-credentials"  // Send cookies with video requests to CloudFront
                  onContextMenu={(e) => e.preventDefault()} // Disable right-click
                  controlsList="nodownload" // Disable download button
                  disablePictureInPicture // Disable PiP
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    width: "auto",
                    height: "auto",
                    objectFit: "contain",
                    background: "rgba(0, 0, 0, 0.9)",
                    borderRadius: 8,
                    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.5)",
                  }}
                />
                {/* Security Indicator */}
                <div
                  style={{
                    position: "absolute",
                    bottom: 40,
                    right: 40,
                    background: "rgba(74, 222, 128, 0.2)",
                    border: "1px solid rgba(74, 222, 128, 0.5)",
                    borderRadius: 8,
                    padding: "8px 12px",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    fontSize: 12,
                    color: "#4ade80",
                    fontWeight: 600,
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="2">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                  </svg>
                  Secure Stream
                </div>
              </>
            ) : (
              <div style={{ textAlign: "center", color: W }}>
                <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="#ff6b6b" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                <p style={{ fontSize: 18, margin: "16px 0 8px 0", color: "#ff6b6b", fontWeight: 600 }}>
                  {videoError}
                </p>
                <p style={{ fontSize: 14, margin: "0 0 16px 0", opacity: 0.7 }}>
                  Please check your connection and try again
                </p>
                <button
                  onClick={() => {
                    setVideoError(null);
                    if (videoRef.current) {
                      videoRef.current.load();
                    }
                  }}
                  style={{
                    padding: "12px 24px",
                    background: "rgba(255, 107, 107, 0.2)",
                    border: "2px solid rgba(255, 107, 107, 0.5)",
                    borderRadius: 8,
                    color: "#ff6b6b",
                    fontSize: 16,
                    fontWeight: 600,
                    cursor: "pointer",
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(255, 107, 107, 0.3)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "rgba(255, 107, 107, 0.2)";
                  }}
                >
                  Retry
                </button>
              </div>
            )}
          </>
        ) : (
          <div style={{ textAlign: "center", color: W }}>
            <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke={W} strokeWidth="1.5">
              <rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18"/>
              <line x1="7" y1="2" x2="7" y2="22"/>
              <line x1="17" y1="2" x2="17" y2="22"/>
              <line x1="2" y1="12" x2="22" y2="12"/>
            </svg>
            <p style={{ fontSize: 16, margin: "16px 0 0 0" }}>
              {fallInfo ? 'Video not available' : 'Please select a fall incident first'}
            </p>
          </div>
        )}
      </div>


    </div>
  );
}
